import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Jewellery details & HUIDs (blocks/niyo-jewellery-details.liquid).
 *
 * The details come from variant metafields that only Liquid can read, so on
 * variant change it swaps in its own freshly rendered markup from the updated
 * section HTML (same approach as product-price).
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} content - Wrapper replaced on variant change
 *
 * @extends {Component<Refs>}
 */
class NiyoJewelleryDetails extends Component {
  requiredRefs = ['content'];

  /** @type {Element | null} */
  #eventTarget = null;

  connectedCallback() {
    super.connectedCallback();
    this.#eventTarget = this.closest('.shopify-section, dialog');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#eventTarget = null;
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const fresh = detail.html.querySelector(
          `niyo-jewellery-details[data-block-id="${this.dataset.blockId}"] [ref="content"]`
        );
        if (!fresh) return;

        // Keep the open/closed state the shopper chose.
        const wasOpen = this.refs.content.querySelector('details')?.open;
        const replacement = /** @type {HTMLElement} */ (fresh.cloneNode(true));
        const details = replacement.querySelector('details');
        if (details && wasOpen !== undefined) details.open = wasOpen;

        this.refs.content.replaceWith(replacement);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[niyo-jewellery-details] Variant update failed:', error);
      });
  };
}

if (!customElements.get('niyo-jewellery-details')) {
  customElements.define('niyo-jewellery-details', NiyoJewelleryDetails);
}
